/**
 * Corporate Actions view — phase 7 of broker integration.
 *
 * Pulls the agent's event stream (BAL /v1/corporate-actions) and renders a
 * chronological list filtered by kind + urgency. Each row computes the
 * estimated impact against the user's local position qty (from the
 * PositionsTable's underlying state).
 */
import { useMemo, useState } from 'react';
import {
  seedDemoActions, sortByEx, labelOf, urgencyOf, daysUntil, impactFor,
  type ActionKind,
} from '../lib/corporate-actions';
import { koboToNaira } from '../lib/format';

const DEMO_POSITIONS: Record<string, number> = {
  ZENITHBANK: 1000, DANGCEM: 50, MTNN: 200, GTCO: 500, BUACEMENT: 100,
  AIRTELAFRI: 80, SEPLAT: 40, FIRSTHOLDCO: 700,
};

const KIND_TABS: Array<'ALL' | ActionKind> = ['ALL', 'CASH_DIV', 'SCRIP_DIV', 'STOCK_SPLIT', 'RIGHTS_ISSUE', 'CAPITAL_REPAY', 'AGM'];

export function CorporateActionsView() {
  const [filter, setFilter] = useState<'ALL' | ActionKind>('ALL');
  const [urgFilter, setUrgFilter] = useState<'ALL' | 'upcoming' | 'past'>('upcoming');
  const actions = useMemo(() => sortByEx(seedDemoActions()), []);
  const now = new Date();

  const filtered = actions.filter((a) => {
    if (filter !== 'ALL' && a.kind !== filter) return false;
    const d = daysUntil(a.exDate, now);
    if (urgFilter === 'upcoming' && d < 0) return false;
    if (urgFilter === 'past' && d >= 0) return false;
    return true;
  });

  const summary = {
    imminent: actions.filter((a) => urgencyOf(a.exDate, now) === 'imminent').length,
    today: actions.filter((a) => urgencyOf(a.exDate, now) === 'today').length,
    upcoming: actions.filter((a) => daysUntil(a.exDate, now) >= 0).length,
    past: actions.filter((a) => daysUntil(a.exDate, now) < 0).length,
  };

  return (
    <div className="ca">
      <div className="ca-head">
        <div>
          <h2>Corporate Actions</h2>
          <div className="ca-sub">
            Dividends, splits, rights issues and AGMs for everything you hold
            or watch. The corporate-actions agent ingests NGX X-Compliance
            filings every 15 minutes; each event is matched to your positions
            and the expected cash or share impact is computed here.
          </div>
        </div>
      </div>

      <div className="ca-summary">
        <span className="rc-pill rc-err">{summary.today} today</span>
        <span className="rc-pill rc-warn">{summary.imminent} within 3 days</span>
        <span className="rc-pill rc-info">{summary.upcoming} upcoming</span>
        <span className="rc-pill rc-ok">{summary.past} past</span>
      </div>

      <div className="ca-filters">
        {KIND_TABS.map((k) => (
          <button key={k} className={'bl-pill ' + (filter === k ? 'active' : '')} onClick={() => setFilter(k)}>
            {k === 'ALL' ? 'All Kinds' : labelOf(k as ActionKind)}
          </button>
        ))}
        <span className="bl-sep" />
        {(['upcoming', 'past', 'ALL'] as const).map((u) => (
          <button key={u} className={'bl-pill ' + (urgFilter === u ? 'active' : '')} onClick={() => setUrgFilter(u)}>{u}</button>
        ))}
      </div>

      <div className="ca-list">
        {filtered.map((a) => {
          const qty = DEMO_POSITIONS[a.symbol] ?? 0;
          const im = impactFor(a, qty);
          const u = urgencyOf(a.exDate, now);
          const d = daysUntil(a.exDate, now);
          return (
            <div key={a.id} className={'ca-card urg-' + u}>
              <div className="ca-card-l">
                <div className="ca-urgency">
                  {d === 0 ? 'TODAY' : d > 0 ? d + 'd' : (-d) + 'd ago'}
                </div>
                <div className={'ca-kind kind-' + a.kind.toLowerCase()}>{labelOf(a.kind)}</div>
              </div>
              <div className="ca-card-m">
                <div className="ca-sym-row">
                  <span className="ca-sym">{a.symbol}</span>
                  {a.ratePerShareKobo && <span className="ca-rate">\u20A6{(Number(a.ratePerShareKobo) / 100).toFixed(2)}/share</span>}
                  {a.ratio && <span className="ca-ratio">{a.ratio.newShares}:{a.ratio.forOld}</span>}
                  {a.newCode && <span className="ca-rate">\u2192 {a.newCode}</span>}
                </div>
                <div className="ca-desc">{a.description}</div>
                <div className="ca-dates">
                  <span>Announced: <strong>{a.announcedAt}</strong></span>
                  <span>Ex-date: <strong>{a.exDate}</strong></span>
                  <span>Record: <strong>{a.recordDate}</strong></span>
                  {a.paymentDate && <span>Payment: <strong>{a.paymentDate}</strong></span>}
                </div>
              </div>
              <div className="ca-card-r">
                <div className="ca-pos">Position: <strong>{qty.toLocaleString('en-NG')}</strong></div>
                {im.cashKobo !== undefined && (
                  <div className="ca-impact cash"><span>Expected cash</span><strong>{koboToNaira(im.cashKobo.toString())}</strong></div>
                )}
                {im.shares !== undefined && (
                  <div className="ca-impact shares"><span>New shares</span><strong>{im.shares.toLocaleString('en-NG')}</strong></div>
                )}
                {im.note && <div className="ca-impact-note">{im.note}</div>}
                {qty === 0 && <div className="ca-impact-note">No position &mdash; watch only.</div>}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="bl-empty">No actions matching filters.</div>}
      </div>
    </div>
  );
}
